import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './components/Login';
import Register from './components/Register';
import ComingSoon from './components/ComingSoon';
import Configuracion from './components/Configuracion';
import Dashboard from './components/Dashboard';
import PreparacionReportes from './components/PreparacionReportes';
import GestionParticipadas from './components/GestionParticipadas';
import GestionDealflow from './components/GestionDealflow';
import ListadoSocios from './components/ListadoSocios';
import RepositorioDocumentos from './components/RepositorioDocumentos';
import Calendar from './components/Calendar';

function App() {
  const [usuario, setUsuario] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');

  // Verificar autenticación al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      
      if (token && userStr) {
        try {
          // Verificar token con el backend
          const response = await fetch('/api/auth/verify', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              setUsuario(data.user);
              setIsAuthenticated(true);
            } else {
              // Token inválido, limpiar
              localStorage.removeItem('token');
              localStorage.removeItem('user');
            }
          } else {
            // Token inválido, limpiar
            localStorage.removeItem('token');
            localStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Error verificando autenticación:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  // Aplicar tema al cargar la aplicación
  useEffect(() => {
    const preferencias = JSON.parse(localStorage.getItem('preferencias') || '{}');
    const tema = preferencias.tema || 'claro';
    
    const aplicarTema = (temaSeleccionado) => {
      if (temaSeleccionado === 'oscuro') {
        document.documentElement.classList.add('dark');
      } else if (temaSeleccionado === 'claro') {
        document.documentElement.classList.remove('dark');
      } else {
        // Sistema: seguir preferencia del sistema
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };
    
    aplicarTema(tema);
    
    // Escuchar cambios en la preferencia del sistema si el tema es "sistema"
    if (tema === 'sistema') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
        if (e.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, []);

  const handleLogin = (user, token) => {
    setUsuario(user);
    setIsAuthenticated(true);
    setShowRegister(false);
  };

  const handleRegister = (user, token) => {
    setUsuario(user);
    setIsAuthenticated(true);
    setShowRegister(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUsuario(null);
    setIsAuthenticated(false);
    setActiveSection('dashboard');
  };

  const getSectionName = (sectionId) => {
    const sectionNames = {
      'dashboard': 'Dashboard',
      'preparacion-reportes': 'Preparación de Reportes',
      'gestion-dealflow': 'Gestión Dealflow',
      'gestion-participadas': 'Gestión Participadas',
      'comunicacion-socios': 'Comunicación con Socios',
      'listado-socios': 'Listado de Socios',
      'repositorio': 'Repositorio de Documentos',
      'calendario': 'Calendario',
      'perfil': 'Perfil',
      'preferencias': 'Preferencias',
      'integraciones': 'Integraciones',
      'plantillas-email': 'Plantillas de Email'
    };
    return sectionNames[sectionId] || 'Sección';
  };

  const renderContent = () => {
    // Dashboard
    if (activeSection === 'dashboard') {
      return <Dashboard onNavigate={setActiveSection} />;
    }
    
    // Si es una sección de configuración, mostrar el componente Configuracion
    if (['configuracion', 'perfil', 'preferencias', 'integraciones', 'plantillas-email'].includes(activeSection)) {
      return <Configuracion vista={activeSection} onNavigate={setActiveSection} />;
    }
    
    // Preparación de Reportes
    if (activeSection === 'preparacion-reportes') {
      return <PreparacionReportes />;
    }
    
    // Gestión Dealflow
    if (activeSection === 'gestion-dealflow') {
      return <GestionDealflow />;
    }
    
    // Gestión Participadas
    if (activeSection === 'gestion-participadas') {
      return <GestionParticipadas />;
    }
    
    // Listado de Socios
    if (activeSection === 'listado-socios') {
      return <ListadoSocios />;
    }
    
    // Repositorio de Documentos
    if (activeSection === 'repositorio') {
      return <RepositorioDocumentos />;
    }
    
    // Calendario
    if (activeSection === 'calendario') {
      return <Calendar />;
    }
    
    // Para el resto, mostrar Coming Soon
    return <ComingSoon sectionName={getSectionName(activeSection)} />;
  };

  // Mostrar loading mientras se verifica autenticación
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  // Mostrar Login o Register si no está autenticado
  if (!isAuthenticated) {
    if (showRegister) {
      return (
        <Register 
          onRegister={handleRegister}
          onSwitchToLogin={() => setShowRegister(false)}
        />
      );
    }
    return (
      <Login 
        onLogin={handleLogin}
        onSwitchToRegister={() => setShowRegister(true)}
      />
    );
  }

  // Mostrar aplicación principal si está autenticado
  return (
    <div className="min-h-screen bg-background">
      <Sidebar onSectionChange={setActiveSection} activeSection={activeSection} />
      <div className="md:ml-72 transition-all duration-300 min-w-0">
        <Header usuario={usuario} onLogout={handleLogout} onNavigate={setActiveSection} />
        <main>
          <div className="container mx-auto px-4 py-8 max-w-6xl">
            <div className="space-y-6">
              {renderContent()}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;

